import SentryService from '@services/SentryService';

import type { DeviceEnumerator } from './DeviceEnumerator';
import type { AcquiredStream, AudioConstraints, NormalizedDevice } from './types';

/** Default audio processing flags optimized for transcription fidelity */
const DEFAULT_CONSTRAINTS: AudioConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/**
 * Acquires a MediaStream with exact→fallback strategy.
 * If the requested device is unavailable, falls back to the system default
 * and reports the substitution.
 */
export class StreamAcquirer {
  constructor(private enumerator: DeviceEnumerator) {}

  /**
   * Acquire a media stream for the given device.
   *
   * Strategy:
   * 1. Try exact deviceId constraint
   * 2. On OverconstrainedError or NotFoundError, retry without constraint
   * 3. Verify the actual device via track.getSettings()
   * 4. Report substitution if the acquired device differs
   */
  async acquire(
    browserDeviceId?: string,
    options?: AudioConstraints,
  ): Promise<AcquiredStream> {
    const constraints = { ...DEFAULT_CONSTRAINTS, ...options };

    // Attempt 1: exact device constraint
    if (browserDeviceId) {
      try {
        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Attempting exact device acquisition',
          data: { deviceId: browserDeviceId },
          level: 'info',
        });

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: browserDeviceId },
            echoCancellation: constraints.echoCancellation,
            noiseSuppression: constraints.noiseSuppression,
            autoGainControl: constraints.autoGainControl,
          },
          video: false,
        });

        return this.buildResult(stream, browserDeviceId);
      } catch (error: unknown) {
        // Only fallback on OverconstrainedError or NotFoundError
        if (error instanceof DOMException) {
          if (error.name === 'OverconstrainedError' || error.name === 'NotFoundError') {
            SentryService.addBreadcrumb({
              category: 'audio.stream',
              message: `Exact acquisition failed (${error.name}), falling back to default`,
              data: { requestedDeviceId: browserDeviceId },
              level: 'warning',
            });
            // Fall through to attempt 2
          } else {
            // Other errors (NotAllowedError, NotReadableError) should propagate
            throw error;
          }
        } else {
          throw error;
        }
      }
    }

    // Attempt 2: no device constraint (system default)
    SentryService.addBreadcrumb({
      category: 'audio.stream',
      message: 'Acquiring stream with system default device',
      level: 'info',
    });

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: constraints.echoCancellation,
        noiseSuppression: constraints.noiseSuppression,
        autoGainControl: constraints.autoGainControl,
      },
      video: false,
    });

    return this.buildResult(stream, browserDeviceId);
  }

  /**
   * Build an AcquiredStream result, detecting substitution by comparing
   * the requested deviceId with the actual device from track.getSettings().
   */
  private buildResult(stream: MediaStream, requestedDeviceId?: string): AcquiredStream {
    const track = stream.getAudioTracks()[0];
    const settings = track?.getSettings();
    const actualDeviceId = settings?.deviceId;

    // Try to find the actual device in our normalized list
    let device: NormalizedDevice | undefined;
    if (actualDeviceId) {
      device = this.enumerator.findByBrowserDeviceId(actualDeviceId);
    }

    // If we can't find the device in our list, create a minimal representation
    if (!device) {
      device = {
        internalId: actualDeviceId || 'unknown',
        browserDeviceId: actualDeviceId || '',
        displayLabel: track?.label || 'Unknown Microphone',
        rawLabel: track?.label || '',
        isDefault: false,
        isCommunications: false,
        transport: 'unknown',
      };
    }

    const wasSubstituted = !!(
      requestedDeviceId &&
      actualDeviceId &&
      requestedDeviceId !== actualDeviceId
    );

    if (wasSubstituted) {
      SentryService.addBreadcrumb({
        category: 'audio.stream',
        message: 'Device substitution detected',
        data: {
          requested: requestedDeviceId,
          actual: actualDeviceId,
          actualLabel: device.displayLabel,
        },
        level: 'warning',
      });
    }

    SentryService.addBreadcrumb({
      category: 'audio.stream',
      message: 'Stream acquired successfully',
      data: {
        deviceId: actualDeviceId,
        label: device.displayLabel,
        wasSubstituted,
        settings: {
          echoCancellation: settings?.echoCancellation,
          noiseSuppression: settings?.noiseSuppression,
          autoGainControl: settings?.autoGainControl,
        },
      },
      level: 'info',
    });

    return {
      stream,
      device,
      wasSubstituted,
      requestedDeviceId,
    };
  }
}
